import axios from "axios";
import { toast } from "react-toastify";

/**
 * Handle bulk import file upload
 * @param {Object} params - Parameters object
 * @param {File} params.bulkImportFile - File to upload
 * @param {string} params.token - Auth token
 * @param {Function} params.setIsBulkImportLoading - Setter for loading state
 * @param {Function} params.setSkippedRowsList - Setter for skipped rows
 * @param {Function} params.setTrackedEntriesCount - Setter for tracked entries count
 * @param {Function} params.setShowSkippedRowsModal - Setter for modal visibility
 * @param {Function} params.refetchTimerData - Function to refetch timer data
 * @param {Function} params.refetchGroupedTimerData - Function to refetch grouped timer data
 * @param {Function} params.viewIsImport - Function to switch view
 * @param {Function} params.handleBulkImportCloseModal - Function to close modal
 * @param {Object} params.toastConfigWithTheme - Toast configuration
 */
export const handleBulkImportUpload = async ({
  bulkImportFile,
  token,
  setIsBulkImportLoading,
  setSkippedRowsList,
  setTrackedEntriesCount,
  setShowSkippedRowsModal,
  refetchTimerData,
  refetchGroupedTimerData,
  viewIsImport,
  handleBulkImportCloseModal,
  toastConfigWithTheme,
}) => {
  setIsBulkImportLoading(true);
  
  if (!bulkImportFile) {
    toast.error("Please select a file.", {
      ...toastConfigWithTheme,
      autoClose: 1500,
    });
    setIsBulkImportLoading(true);
    return;
  }

  const fileExtension = bulkImportFile.name.split(".").pop();
  if (fileExtension !== "xlsx") {
    toast.error("Only xlsx files are supported.", {
      ...toastConfigWithTheme,
      autoClose: 1500,
    });
    setIsBulkImportLoading(false);
    return;
  }

  const formData = new FormData();
  formData.append("file", bulkImportFile);
  formData.append("tz", global.config.getTimeZone());

  try {
    const response = await axios.post(
      "/api/time-track/importdata/grouped/import/timer",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      }
    );
    
    if (response.data.skippedRows && response.data.skippedRows.length > 0) {
      setSkippedRowsList(response.data.skippedRows);
      setTrackedEntriesCount(
        response.data.trackedEntries ? response.data.trackedEntries.length : 0
      );
      setShowSkippedRowsModal(true);
    }

    if (
      response.data.trackedEntries &&
      response.data.trackedEntries.length > 0
    ) {
      toast.success(
        `Inserted ${response.data.trackedEntries.length} records`,
        {
          ...toastConfigWithTheme,
          autoClose: 5000,
        }
      );
      refetchTimerData();
      refetchGroupedTimerData();
      if (!response.data.skippedRows?.length) {
        viewIsImport();
        handleBulkImportCloseModal();
      }
    } else {
      toast.success(
        "File upload was unsuccessful: no valid data found.",
        {
          ...toastConfigWithTheme,
          autoClose: 1500,
        }
      );
    }
  } catch (error) {
    // Handle validation errors (400 status) with specific error message
    if (error.response?.status === 400 && error.response?.data?.status === "requiredValidationError") {
      toast.error(error.response.data.message, {
        ...toastConfigWithTheme,
        autoClose: 5000,
      });
    } else {
      console.log(error);
      global.config.slackMessage(error.toString());
      toast.error("Error uploading file. Please try again.", {
        ...toastConfigWithTheme,
        autoClose: 1500,
      });
    }
  } finally {
    setIsBulkImportLoading(false);
  }
};

/**
 * Handle bulk import file selection
 * @param {Event} e - File input change event
 * @param {Function} setBulkImportFile - Setter for file state
 * @param {Function} setBulkImportFileName - Setter for file name state
 */
export const handleBulkImportFileSelect = (e, setBulkImportFile, setBulkImportFileName) => {
  try {
    const file = e.target.files[0];

    if (file) {
      setBulkImportFile(file);
      setBulkImportFileName(file.name);
    } else {
      console.warn("handleBulkImportFileSelect: No file selected.");
      setBulkImportFile(null);
      setBulkImportFileName("");
    }
  } catch (error) {
    console.error("handleBulkImportFileSelect: Unexpected error", error);
    setBulkImportFile(null);
    setBulkImportFileName("");
  }
};
